import React, { useState } from 'react'
import { Typography, Button, Skeleton, Tooltip, Select } from 'antd'
import { ArrowRightOutlined, CheckCircleOutlined, ExclamationCircleOutlined, MinusCircleOutlined, TeamOutlined, DownOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import { IUserWorkingHours } from '@vacationtracker/shared/types/work-week'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { HourFormatEnum } from '@vacationtracker/shared/types/user'
import { formatTimeString } from '@vacationtracker/shared/functions/get-days-or-hours'
import { convertHourFormats } from '@vacationtracker/shared/functions/convert-between-hour-formats'

dayjs.extend(isSameOrBefore)

const { Title } = Typography
const { Option } = Select

// Simple skeleton loader for team availability
const TeamAvailabilitySkeleton: React.FC = () => (
  <div className="team-availability-skeleton">
    {/* Title skeleton */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
      <Skeleton.Input style={{ width: 180, height: 20 }} active />
      <Skeleton.Input style={{ width: 100, height: 16 }} active />
    </div>

    {/* Content skeleton */}
    <Skeleton active paragraph={{ rows: 4, width: ['100%', '90%', '95%', '88%', '92%', '85%'] }} title={false} />
  </div>
)

interface TeamMember {
  id: string
  name: string
  imageUrl?: string
  isOut?: boolean
  leaveType?: {
    name: string
    color: string
  }
  startDate?: string
  endDate?: string
  isPartDay?: boolean
  partDay?: {
    startHour: number
    startMinute: number
    endHour: number
    endMinute: number
  }
}

interface TeamLeaveData {
  id: string
  user: {
    id: string
    name: string
    imageUrl?: string
  }
  leaveType: {
    name: string
    color: string
  }
  startDate: string
  endDate: string
  isPartDay: boolean
  partDay?: {
    startHour: number
    startMinute: number
    endHour: number
    endMinute: number
  }
  workingDays: number
}

interface TeamUser {
  id: string
  name: string
  status: string
  locationId?: string
}

interface Location {
  id: string
  name: string
  holidays?: Array<{
    year: number
    holidays: Array<{
      name: string
      date: string
      isHalfDay?: boolean
    }>
  }>
}

interface TeamAvailabilityProps {
  teamName?: string // Department/team name
  todayOut: TeamMember[]
  allTeamLeaves: TeamLeaveData[]
  allTeamUsers?: TeamUser[] // All users in the department
  allCompanyLeaves?: TeamLeaveData[] // All leaves for the entire company
  allCompanyUsers?: TeamUser[] // All users in the company
  locations?: Location[] // All locations with their holidays
  currentUserWorkHours?: IUserWorkingHours // Current user's working hours for determining working days
  viewerHourFormat?: HourFormatEnum
  isLoading?: boolean
  className?: string
  visibilityLevel?: string | null // Visibility addon level
  onViewScopeChange?: (scope: 'department' | 'everyone') => void // Callback when view scope changes
}

const TeamAvailabilityComponent: React.FC<TeamAvailabilityProps> = ({
  teamName,
  todayOut,
  allTeamLeaves,
  allTeamUsers = [],
  allCompanyLeaves = [],
  allCompanyUsers = [],
  locations = [],
  currentUserWorkHours,
  viewerHourFormat = HourFormatEnum.twentyFour,
  isLoading = false,
  className = '',
  visibilityLevel = null,
  onViewScopeChange,
}) => {
  const { formatMessage } = useIntl()
  const [viewScope, setViewScope] = useState<'department' | 'everyone'>('department')

  // Check if "Everyone" option should be disabled
  const isEveryoneDisabled = visibilityLevel === 'DEPARTMENT'

  // Use company or team data based on view scope
  const currentLeaves = viewScope === 'everyone' ? allCompanyLeaves : allTeamLeaves
  const currentUsers = viewScope === 'everyone' ? allCompanyUsers : allTeamUsers

  // Handle view scope change
  const handleViewScopeChange = (value: 'department' | 'everyone') => {
    // Don't allow selecting "everyone" if it's disabled
    if (value === 'everyone' && isEveryoneDisabled) {
      return
    }

    setViewScope(value)
    if (onViewScopeChange) {
      onViewScopeChange(value)
    }
  }

  // Helper function to get holidays for users on a specific date
  const getHolidaysForDate = (date: dayjs.Dayjs, users: TeamUser[], locs: Location[]) => {
    const dateStr = date.format('YYYY-MM-DD')
    const year = date.year()
    const holidaysByLocation: Map<
      string,
      {
        holiday: { name: string; isHalfDay?: boolean }
        location: string
        users: string[]
      }
    > = new Map()

    users.forEach((user) => {
      if (user.locationId) {
        const location = locs.find((l) => l.id === user.locationId)
        if (location?.holidays) {
          const yearHolidays = location.holidays.find((h) => h.year === year)
          if (yearHolidays) {
            const holiday = yearHolidays.holidays.find((h) => h.date === dateStr)
            if (holiday) {
              const locationKey = `${holiday.name}_${location.id}`
              const existing = holidaysByLocation.get(locationKey)
              if (existing) {
                existing.users.push(user.name)
              } else {
                holidaysByLocation.set(locationKey, {
                  holiday: { name: holiday.name, isHalfDay: holiday.isHalfDay },
                  location: location.name,
                  users: [user.name],
                })
              }
            }
          }
        }
      }
    })

    return Array.from(holidaysByLocation.values()).map(({ holiday, location, users }) => ({
      name: holiday.name,
      isHalfDay: holiday.isHalfDay,
      location,
      affectedUsers: users,
    }))
  }

  // Helper function to check if a user is on holiday on a specific date
  const isUserOnHoliday = (userId: string, date: dayjs.Dayjs) => {
    const user = currentUsers.find((u) => u.id === userId)
    if (!user?.locationId) return false

    const location = locations.find((l) => l.id === user.locationId)
    if (!location?.holidays) return false

    const dateStr = date.format('YYYY-MM-DD')
    const year = date.year()
    const yearHolidays = location.holidays.find((h) => h.year === year)

    if (!yearHolidays) return false

    return yearHolidays.holidays.some((h) => h.date === dateStr)
  }

  // Helper function to get the current week's working hours
  const getCurrentWeekWorkingHours = (targetDate: dayjs.Dayjs) => {
    if (!currentUserWorkHours) {
      return null
    }

    // If user has a rotating schedule, use that
    if (currentUserWorkHours.schedule?.workHours) {
      const schedule = currentUserWorkHours.schedule
      const startDate = dayjs(schedule.startDate)

      // Calculate which week we're in since the schedule started
      const weeksSinceStart = targetDate.diff(startDate, 'week')
      const scheduleWeekIndex = weeksSinceStart % schedule.workHours.length

      return schedule.workHours[scheduleWeekIndex]
    }

    // Otherwise use the static working hours
    return currentUserWorkHours.workHours
  }

  // Helper function to determine if a day is a working day based on current user's schedule
  const isWorkingDay = (dayOfWeek: number, targetDate: dayjs.Dayjs): boolean => {
    // dayOfWeek: 0 = Sunday, 1 = Monday, 2 = Tuesday, etc.

    const workingHours = getCurrentWeekWorkingHours(targetDate)

    // If no working hours specified, assume standard Monday-Friday
    if (!workingHours || !Array.isArray(workingHours)) {
      return dayOfWeek >= 1 && dayOfWeek <= 5 // Monday to Friday
    }

    // The working hours structure is complex - let's parse it
    // Each day is represented as [day, hours] where both can be objects with parsedValue
    for (const dayEntry of workingHours) {
      if (Array.isArray(dayEntry) && dayEntry.length === 2) {
        const [dayObj, hoursObj] = dayEntry

        // Extract the parsed values - use type assertion to handle the complex structure
        const parsedDayObj = dayObj as { parsedValue?: number }
        const parsedHoursObj = hoursObj as { parsedValue?: number }
        const day = parsedDayObj?.parsedValue !== undefined ? parsedDayObj.parsedValue : dayObj
        const hours = parsedHoursObj?.parsedValue !== undefined ? parsedHoursObj.parsedValue : hoursObj

        // Check if this matches our target day and has working hours
        if (day === dayOfWeek && hours > 0) {
          return true
        }
      }
    }

    return false
  }

  // Helper: format part-day range in viewer's hour format
  const formatPartDayRange = (startHour: number, startMinute: number | undefined, endHour: number, endMinute: number | undefined): { start: string; end: string } => {
    const startConverted = convertHourFormats(viewerHourFormat, startHour, startMinute ?? 0)
    const endConverted = convertHourFormats(viewerHourFormat, endHour, endMinute ?? 0)
    const start = formatTimeString({
      hour: startConverted.value,
      minute: startConverted.minute,
      showZeroPadding: false,
      hourFormat: viewerHourFormat,
      amOrPm: viewerHourFormat === HourFormatEnum.twelve ? startConverted.amOrPm : undefined,
    })
    const end = formatTimeString({
      hour: endConverted.value,
      minute: endConverted.minute,
      showZeroPadding: false,
      hourFormat: viewerHourFormat,
      amOrPm: viewerHourFormat === HourFormatEnum.twelve ? endConverted.amOrPm : undefined,
    })
    return { start, end }
  }

  // Debug: Component props - moved below variable declarations
  const today = dayjs()

  // Check if there are any leaves in the next 2 weeks (excluding people on holiday)
  const hasLeavesInNext2Weeks = (() => {
    // Check if anyone is out today (after filtering holidays)
    const todayHasLeaves = todayOut.some((member) => !isUserOnHoliday(member.id, today))
    if (todayHasLeaves) return true

    // Check if anyone is out in the next 2 weeks (after filtering holidays)
    const endDate = today.add(2, 'weeks')
    return currentLeaves.some((leave) => {
      const leaveStart = dayjs(leave.startDate)
      const leaveEnd = dayjs(leave.endDate)

      // Check if leave is within next 2 weeks
      if (leaveEnd.isBefore(today) || leaveStart.isAfter(endDate)) {
        return false
      }

      // Check if the user is on holiday for ALL days of their leave
      // If they're on holiday for any day of their leave, we'll still show them
      // because they have non-holiday leave days
      let currentDay = leaveStart.isBefore(today) ? today : leaveStart
      while (currentDay.isSameOrBefore(leaveEnd) && currentDay.isSameOrBefore(endDate)) {
        if (!isUserOnHoliday(leave.user.id, currentDay)) {
          return true // User has at least one non-holiday leave day
        }
        currentDay = currentDay.add(1, 'day')
      }

      return false // User is on holiday for all their leave days
    })
  })()

  // Calculate coverage based on actual team size
  const getTeamSize = () => {
    const activeUsers = currentUsers.filter((user) => user.status === 'ACTIVE')
    return activeUsers.length
  }

  const getCoverageStatus = (peopleOut: number, totalTeam: number, isWeeklyView = false) => {
    if (totalTeam === 0)
      return {
        text: '',
        percentage: 100,
        color: '#6b7280',
        icon: <CheckCircleOutlined />,
      }

    const availableCount = totalTeam - peopleOut
    const percentage = Math.round((availableCount / totalTeam) * 100)

    // For weekly view, don't show "Full team" if anyone is out
    if (percentage === 100 && (!isWeeklyView || !hasAnyoneOutInWeek)) {
      return {
        text: formatMessage({ id: 'dashboard.teamAvailability.coverage.fullTeam' }),
        percentage,
        color: '#6b7280',
        icon: <CheckCircleOutlined />,
      }
    } else if (percentage >= 75) {
      return {
        text: formatMessage({ id: 'dashboard.teamAvailability.coverage.good' }),
        percentage,
        color: '#6b7280',
        icon: <CheckCircleOutlined />,
      }
    } else if (percentage >= 50) {
      return {
        text: formatMessage({ id: 'dashboard.teamAvailability.coverage.medium' }),
        percentage,
        color: '#6b7280',
        icon: <ExclamationCircleOutlined />,
      }
    } else {
      return {
        text: formatMessage({ id: 'dashboard.teamAvailability.coverage.low' }),
        percentage,
        color: '#6b7280',
        icon: <MinusCircleOutlined />,
      }
    }
  }

  const totalTeamSize = getTeamSize()
  const hasCoverageData = totalTeamSize > 0

  // Calculate next week date range
  const nextMonday = today.add(1, 'week').startOf('week').add(1, 'day') // Next Monday
  const nextSunday = nextMonday.add(6, 'days') // Next Sunday

  // Build today's out list based on scope (department vs everyone)
  const todayOutBase =
    viewScope === 'everyone'
      ? currentLeaves
          .filter((leave) => {
            const startDate = dayjs(leave.startDate).startOf('day')
            const endDate = dayjs(leave.endDate).startOf('day')
            const workingDay = today.startOf('day')

            const overlapsWithDay =
              (startDate.isSame(workingDay, 'day') || startDate.isBefore(workingDay, 'day')) && (endDate.isSame(workingDay, 'day') || endDate.isAfter(workingDay, 'day'))
            const onHoliday = isUserOnHoliday(leave.user.id, workingDay)

            return overlapsWithDay && !onHoliday
          })
          .map((leave) => ({
            id: leave.user.id,
            name: leave.user.name,
            imageUrl: leave.user.imageUrl,
            isOut: true,
            leaveType: leave.leaveType,
            startDate: leave.startDate,
            endDate: leave.endDate,
            isPartDay: leave.isPartDay,
            partDay: leave.partDay,
          }))
      : todayOut

  // Ensure users on a public holiday today are excluded; this is especially needed for department scope,
  // where the parent-provided `todayOut` may not have removed public-holiday users.
  const filteredTodayOut = todayOutBase.filter((member) => !isUserOnHoliday(member.id, today))

  // Calculate daily breakdown for next week
  const getNextWeekDailyBreakdown = () => {
    const weekdays: Array<{
      date: dayjs.Dayjs
      dayName: string
      peopleOut: number
      peopleWorking: number
      peopleAvailable: number
      peopleOutNames: string[]
      peopleOutDetails: Array<{
        name: string
        startDate: string
        endDate: string
        isPartDay: boolean
        partDay?: {
          startHour: number
          startMinute: number
          endHour: number
          endMinute: number
        }
      }>
      holidays?: Array<{
        name: string
        isHalfDay?: boolean
        location: string
        affectedUsers: string[]
      }>
      isWorkingDay: boolean
    }> = []

    // Generate all 7 days in next week (Monday to Sunday)
    for (let i = 0; i < 7; i++) {
      const currentDay = nextMonday.add(i, 'days')

      // Get people out on this day (excluding those on public holiday)
      const peopleOutToday = currentLeaves.filter((leave) => {
        const startDate = dayjs(leave.startDate).startOf('day')
        const endDate = dayjs(leave.endDate).startOf('day')

        // Check if leave overlaps with this day
        const overlapsWithDay =
          (startDate.isSame(currentDay, 'day') || startDate.isBefore(currentDay, 'day')) && (endDate.isSame(currentDay, 'day') || endDate.isAfter(currentDay, 'day'))

        // Exclude users who are on public holiday on this day
        const onHoliday = isUserOnHoliday(leave.user.id, currentDay)

        return overlapsWithDay && !onHoliday
      })

      // Group by user and extract details
      const userLeaveMap = new Map<string, TeamLeaveData>()
      peopleOutToday.forEach((leave) => {
        const existing = userLeaveMap.get(leave.user.name)
        if (!existing || dayjs(leave.startDate).isAfter(dayjs(existing.startDate))) {
          userLeaveMap.set(leave.user.name, leave)
        }
      })

      const uniqueUserNames = Array.from(userLeaveMap.keys()).sort()
      const peopleOutDetails = uniqueUserNames
        .map((name) => {
          const leave = userLeaveMap.get(name)
          if (!leave) return null
          return {
            name,
            startDate: leave.startDate,
            endDate: leave.endDate,
            isPartDay: leave.isPartDay,
            partDay: leave.partDay,
          }
        })
        .filter((detail): detail is NonNullable<typeof detail> => detail !== null)

      // Calculate how many people are working on this day (all active team members on current user's working days)
      const dayOfWeek = currentDay.day() // 0 = Sunday, 1 = Monday, etc.
      const isWorkingDayForUser = isWorkingDay(dayOfWeek, currentDay)

      // Skip non-working days entirely
      if (!isWorkingDayForUser) {
        continue
      }

      const peopleWorkingToday = currentUsers.filter((user) => user.status === 'ACTIVE').length

      // Get holidays for this day
      const holidaysToday = getHolidaysForDate(currentDay, currentUsers, locations)

      weekdays.push({
        date: currentDay,
        dayName: currentDay.format('dddd'),
        peopleOut: uniqueUserNames.length,
        peopleWorking: peopleWorkingToday,
        peopleAvailable: Math.max(0, peopleWorkingToday - uniqueUserNames.length - holidaysToday.reduce((sum, h) => sum + h.affectedUsers.length, 0)),
        peopleOutNames: uniqueUserNames,
        peopleOutDetails,
        holidays: holidaysToday,
        isWorkingDay: true, // All days we push are working days now
      })
    }

    return weekdays
  }

  const nextWeekDailyBreakdown = getNextWeekDailyBreakdown()

  // Calculate average coverage for next week
  const calculateAverageCoverage = () => {
    if (nextWeekDailyBreakdown.length === 0 || totalTeamSize === 0) return 100

    const totalAvailable = nextWeekDailyBreakdown.reduce((sum, day) => sum + day.peopleAvailable, 0)
    const totalPossible = nextWeekDailyBreakdown.length * totalTeamSize

    return Math.ceil((totalAvailable / totalPossible) * 100)
  }

  const averageCoverage = calculateAverageCoverage()

  // Check if anyone is out during the week (for "Full team" logic)
  const hasAnyoneOutInWeek = nextWeekDailyBreakdown.some((day) => day.peopleOut > 0)

  // Get the next working day (tomorrow or next weekday)
  const getNextWorkingDay = () => {
    const tomorrow = dayjs().add(1, 'day')
    const dayOfWeek = tomorrow.day() // 0 = Sunday, 6 = Saturday

    // If tomorrow is Saturday (6), move to Monday
    if (dayOfWeek === 6) {
      return tomorrow.add(2, 'days')
    }
    // If tomorrow is Sunday (0), move to Monday
    if (dayOfWeek === 0) {
      return tomorrow.add(1, 'day')
    }
    // Tomorrow is a weekday
    return tomorrow
  }

  const nextWorkingDay = getNextWorkingDay()
  const isTomorrowWorkingDay = nextWorkingDay.isSame(dayjs().add(1, 'day'), 'day')

  // Get holidays for today
  const todayHolidays = getHolidaysForDate(today, currentUsers, locations)

  // Get holidays for tomorrow/next working day
  const nextWorkingDayHolidays = getHolidaysForDate(nextWorkingDay, currentUsers, locations)

  // Get people out on the next working day (excluding those on public holiday)
  const getNextWorkingDayOut = () => {
    return currentLeaves
      .filter((leave) => {
        const startDate = dayjs(leave.startDate).startOf('day')
        const endDate = dayjs(leave.endDate).startOf('day')
        const workingDay = nextWorkingDay.startOf('day')

        // Check if the leave overlaps with the next working day
        const overlapsWithDay =
          (startDate.isSame(workingDay, 'day') || startDate.isBefore(workingDay, 'day')) && (endDate.isSame(workingDay, 'day') || endDate.isAfter(workingDay, 'day'))

        // Exclude users who are on public holiday on this day
        const onHoliday = isUserOnHoliday(leave.user.id, workingDay)

        return overlapsWithDay && !onHoliday
      })
      .map((leave) => ({
        id: leave.user.id,
        name: leave.user.name,
        imageUrl: leave.user.imageUrl,
        isOut: true,
        leaveType: leave.leaveType,
        startDate: leave.startDate,
        endDate: leave.endDate,
        isPartDay: leave.isPartDay,
        partDay: leave.partDay,
      }))
  }

  const nextWorkingDayOut = getNextWorkingDayOut()

  // Special render function for Next Week card with daily breakdown
  const renderNextWeekCard = (
    title: string,
    dailyBreakdown: Array<{
      date: dayjs.Dayjs
      dayName: string
      peopleOut: number
      peopleWorking: number
      peopleAvailable: number
      peopleOutNames: string[]
      peopleOutDetails: Array<{
        name: string
        startDate: string
        endDate: string
        isPartDay: boolean
        partDay?: {
          startHour: number
          startMinute: number
          endHour: number
          endMinute: number
        }
      }>
      holidays?: Array<{
        name: string
        isHalfDay?: boolean
        location: string
        affectedUsers: string[]
      }>
      isWorkingDay: boolean
    }>,
    coverageStatus?: {
      text: string
      percentage: number
      color: string
      icon: React.ReactNode
    }
  ) => (
    <div className="team-card">
      {/* Header */}
      <div className="team-card-header">
        <span className="team-card-title">{title}</span>
        {coverageStatus && (
          <span className="team-card-coverage">
            <span className="coverage-icon">{coverageStatus.icon || <CheckCircleOutlined />}</span>
            {coverageStatus.text}
          </span>
        )}
      </div>

      {/* Daily breakdown */}
      <div className="team-card-content">
        <div className="daily-breakdown">
          {dailyBreakdown.map((day) => (
            <div key={day.date.format('YYYY-MM-DD')} className="daily-breakdown-item">
              <span className="day-name">{day.dayName}:</span>
              <span className="day-status">
                {day.peopleOut === 0 && (!day.holidays || day.holidays.length === 0) ? (
                  formatMessage({ id: 'dashboard.teamAvailability.everyoneAvailable' })
                ) : day.peopleOutDetails.length > 0 || (day.holidays && day.holidays.length > 0) ? (
                  <Tooltip
                    title={
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {/* Show holiday info first if any */}
                        {day.holidays && day.holidays.length > 0 && (
                          <div style={{ marginBottom: day.peopleOutDetails.length > 0 ? 12 : 0 }}>
                            {day.holidays.map((holiday, hIdx) => (
                              <div key={hIdx} style={{ marginBottom: hIdx < (day.holidays?.length || 0) - 1 ? 8 : 0 }}>
                                <div style={{ fontWeight: 600 }}>
                                  🗓️ {holiday.name} ({holiday.location})
                                </div>
                                <div style={{ fontSize: '12px', opacity: 0.8 }}>{day.date.format('ddd, MMM D, YYYY')}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {day.peopleOutDetails.map((person, index) => {
                          const startDate = dayjs(person.startDate)
                          const endDate = dayjs(person.endDate)
                          const isSingleDay = startDate.isSame(endDate, 'day')

                          return (
                            <div key={index} style={{ marginBottom: index < day.peopleOutDetails.length - 1 ? '8px' : 0 }}>
                              <div>{person.name}</div>
                              <div style={{ fontSize: '12px', opacity: 0.8 }}>
                                {person.isPartDay && person.partDay
                                  ? (() => {
                                      const range = formatPartDayRange(person.partDay.startHour, person.partDay.startMinute, person.partDay.endHour, person.partDay.endMinute)
                                      return `${startDate.format('ddd, MMM D, YYYY')} - ${range.start} - ${range.end}`
                                    })()
                                  : isSingleDay
                                    ? // Single full day
                                      startDate.format('ddd, MMM D, YYYY')
                                    : // Multiple days
                                      `${startDate.format('ddd, MMM D, YYYY')} - ${endDate.format('ddd, MMM D, YYYY')}`}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    }
                    placement="top"
                  >
                    <span style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}>
                      {formatMessage(
                        { id: 'dashboard.teamAvailability.peopleOut' },
                        { count: day.peopleOut + (day.holidays?.reduce((sum, h) => sum + h.affectedUsers.length, 0) || 0) }
                      )}
                    </span>
                  </Tooltip>
                ) : (
                  formatMessage({ id: 'dashboard.teamAvailability.peopleOut' }, { count: day.peopleOut + (day.holidays?.reduce((sum, h) => sum + h.affectedUsers.length, 0) || 0) })
                )}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Average summary */}
      <div className="team-card-footer">
        {dailyBreakdown.length > 0 &&
          formatMessage(
            { id: 'dashboard.teamAvailability.averageAvailable' },
            {
              average: Math.round(dailyBreakdown.reduce((sum, day) => sum + day.peopleAvailable, 0) / dailyBreakdown.length),
              total: Math.round(dailyBreakdown.reduce((sum, day) => sum + day.peopleWorking, 0) / dailyBreakdown.length),
            }
          )}
      </div>
    </div>
  )

  const renderTeamCard = (
    title: string,
    members: TeamMember[],
    coverageStatus?: {
      text: string
      percentage: number
      color: string
      icon: React.ReactNode
    },
    isToday?: boolean,
    showDates?: boolean,
    holidays?: Array<{
      name: string
      isHalfDay?: boolean
      location: string
      affectedUsers: string[]
    }>
  ) => (
    <div className="team-card">
      {/* Header */}
      <div className="team-card-header">
        <span className="team-card-title">{title}</span>
        {coverageStatus && (
          <span className="team-card-coverage">
            <span className="coverage-icon">{coverageStatus.icon || <CheckCircleOutlined />}</span>
            {coverageStatus.text}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="team-card-content">
        {members.length > 0 || (holidays && holidays.length > 0) ? (
          <>
            <div className="team-members-list">
              <div className="team-members-container">
                {/* Holidays - displayed at the top */}
                {holidays &&
                  holidays.length > 0 &&
                  holidays.map((holiday, idx) => (
                    <div key={`holiday-${idx}`} className="team-member-item">
                      <div className="member-avatar">🗓️</div>
                      <div className="member-info">
                        <div className="member-name">
                          <span>
                            {holiday.name} ({holiday.location})
                          </span>
                          {holiday.isHalfDay && <span className="member-until-date">(Half day)</span>}
                        </div>
                        <span style={{ fontSize: '12px', opacity: 0.7 }}>
                          {holiday.affectedUsers.length}{' '}
                          {formatMessage({
                            id: holiday.affectedUsers.length === 1 ? 'dashboard.teamAvailability.person' : 'dashboard.teamAvailability.people',
                          })}
                        </span>
                      </div>
                    </div>
                  ))}

                {/* Members */}
                {members.slice(0, 4).map((member) => (
                  <Tooltip key={member.id} title={member.leaveType?.name} placement="top">
                    <div className="team-member-item" style={{ cursor: 'pointer' }}>
                      <div className="member-avatar">
                        {member.imageUrl ? (
                          <img
                            src={member.imageUrl}
                            alt={member.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: 'inherit',
                            }}
                            onError={(e) => {
                              // Fallback to initials if image fails to load
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                              const initials = member.name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .toUpperCase()
                              const span = document.createElement('span')
                              span.textContent = initials
                              span.style.width = '100%'
                              span.style.height = '100%'
                              span.style.display = 'flex'
                              span.style.alignItems = 'center'
                              span.style.justifyContent = 'center'
                              target.parentElement?.appendChild(span)
                            }}
                          />
                        ) : (
                          member.name
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .toUpperCase()
                        )}
                      </div>
                      <div className="member-info">
                        <div className="member-name">
                          <span>{member.name}</span>
                          {(() => {
                            const isSingleDay = member.startDate && member.endDate ? dayjs(member.startDate as string).isSame(dayjs(member.endDate as string), 'day') : false
                            // If part-day/hourly, always show both times: "from {start} to {end}"
                            if (member.isPartDay && member.partDay) {
                              const { start: startTime, end: endTime } = formatPartDayRange(
                                member.partDay.startHour,
                                member.partDay.startMinute,
                                member.partDay.endHour,
                                member.partDay.endMinute
                              )
                              return (
                                <span className="member-until-date">({formatMessage({ id: 'dashboard.teamAvailability.fromUntilTime' }, { start: startTime, end: endTime })})</span>
                              )
                            }
                            // For single full-day leaves, do not show "until"
                            if (member.endDate && !isSingleDay) {
                              return (
                                <span className="member-until-date">
                                  ({formatMessage({ id: 'dashboard.teamAvailability.until' }, { date: dayjs(member.endDate).format('dddd, MMM D') })})
                                </span>
                              )
                            }
                            return null
                          })()}
                        </div>
                        {showDates && member.startDate && <span className="member-start-date">{dayjs(member.startDate).format('MMM DD')}</span>}
                      </div>
                    </div>
                  </Tooltip>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="team-card-footer">
              {formatMessage({ id: 'dashboard.teamAvailability.available' }, { available: totalTeamSize - members.length, total: totalTeamSize })}
            </div>
          </>
        ) : (
          <div className="everyone-available">{formatMessage({ id: 'dashboard.teamAvailability.everyoneAvailable' })}</div>
        )}
      </div>
    </div>
  )

  // Show skeleton loader while loading
  if (isLoading) {
    return <TeamAvailabilitySkeleton />
  }

  return (
    <div className={`team-availability ${className}`} style={{ marginBottom: 24 }}>
      <div className="team-availability-header">
        <div className="team-availability-header-left">
          <div className="team-availability-title-wrapper">
            <TeamOutlined style={{ marginRight: 8 }} />
            <Title level={4} className="team-availability-title" style={{ margin: 0 }}>
              {formatMessage({ id: 'dashboard.teamAvailability.title' })}
            </Title>
          </div>
          <Select className="team-availability-select" value={viewScope} onChange={handleViewScopeChange} suffixIcon={<DownOutlined />} style={{ minWidth: 200 }}>
            <Option value="department">{teamName ? `${teamName} ${formatMessage({ id: 'app.department' }).toLowerCase()}` : formatMessage({ id: 'app.department' })}</Option>
            <Option value="everyone" disabled={isEveryoneDisabled}>
              {isEveryoneDisabled ? (
                <Tooltip title={formatMessage({ id: 'dashboard.teamAvailability.everyoneDisabledTooltip' })}>
                  <span>{formatMessage({ id: 'dashboard.teamAvailability.everyone' })}</span>
                </Tooltip>
              ) : (
                formatMessage({ id: 'dashboard.teamAvailability.everyone' })
              )}
            </Option>
          </Select>
        </div>
        <Button type="link" href="/app/calendar" className="team-availability-calendar-link" size="small">
          {formatMessage({ id: 'dashboard.fullCalendar' })} <ArrowRightOutlined />
        </Button>
      </div>

      {!hasLeavesInNext2Weeks ? (
        // Show single card when no one is out
        <div className="no-leaves-card">
          <div className="no-leaves-icon">✨</div>
          <div className="no-leaves-message">{formatMessage({ id: 'dashboard.teamAvailability.noLeaves.title' })}</div>
          <div className="no-leaves-subtitle">{formatMessage({ id: 'dashboard.teamAvailability.noLeaves.description' })}</div>
        </div>
      ) : (
        // Show 3 cards when people are out
        <div className="team-availability-grid">
          {renderTeamCard(
            formatMessage({ id: 'dashboard.teamAvailability.today' }, { date: dayjs().format('ddd MMM D') }),
            filteredTodayOut,
            hasCoverageData ? getCoverageStatus(filteredTodayOut.length + todayHolidays.reduce((sum, h) => sum + h.affectedUsers.length, 0), totalTeamSize) : undefined,
            true,
            false,
            todayHolidays
          )}
          {renderTeamCard(
            isTomorrowWorkingDay ? formatMessage({ id: 'dashboard.teamAvailability.tomorrow' }, { date: nextWorkingDay.format('ddd MMM D') }) : nextWorkingDay.format('ddd MMM D'), // Just show the date if it's not tomorrow
            nextWorkingDayOut, // Use our calculated data instead of tomorrowOut
            hasCoverageData ? getCoverageStatus(nextWorkingDayOut.length + nextWorkingDayHolidays.reduce((sum, h) => sum + h.affectedUsers.length, 0), totalTeamSize) : undefined,
            false,
            false,
            nextWorkingDayHolidays
          )}
          {renderNextWeekCard(
            `${formatMessage({ id: 'dashboard.teamAvailability.nextWeek' })} (${nextMonday.format('MMM DD')} - ${nextSunday.format('MMM DD')})`,
            nextWeekDailyBreakdown,
            hasCoverageData ? getCoverageStatus(Math.round(((100 - averageCoverage) * totalTeamSize) / 100), totalTeamSize, true) : undefined
          )}
        </div>
      )}
    </div>
  )
}

// Memoize component to prevent unnecessary re-renders
// Only re-render when team data or loading state changes
const TeamAvailability = React.memo(TeamAvailabilityComponent, (prevProps, nextProps) => {
  // Custom comparison function - return true if props are equal (skip re-render)
  return (
    // Check array references - assumes immutable updates
    prevProps.todayOut === nextProps.todayOut &&
    prevProps.allTeamLeaves === nextProps.allTeamLeaves &&
    prevProps.allTeamUsers === nextProps.allTeamUsers &&
    prevProps.allCompanyLeaves === nextProps.allCompanyLeaves &&
    prevProps.allCompanyUsers === nextProps.allCompanyUsers &&
    prevProps.locations === nextProps.locations &&
    // Check primitive props
    prevProps.teamName === nextProps.teamName &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.className === nextProps.className &&
    prevProps.visibilityLevel === nextProps.visibilityLevel &&
    prevProps.onViewScopeChange === nextProps.onViewScopeChange
  )
})

// Set display name for debugging
TeamAvailability.displayName = 'TeamAvailability'

export default TeamAvailability
